import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import MediaKitEditor from './MediaKitEditor';

const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return `${n}`;
};

/**
 * How complete the page is, as the list of things still missing.
 *
 * A bare percentage tells a creator they are at 60% without telling them what
 * to do about it, so the bar is backed by the actual to-do list. Reach and
 * engagement count as one item: a media kit without them is just a profile.
 */
function checklist(data, t) {
    const kit = data?.kit;
    const metrics = data?.metrics;
    return [
        { key: 'headline', done: Boolean(kit?.headline), label: t('mediaKit.checkHeadline') },
        { key: 'bio', done: Boolean(kit?.bio), label: t('mediaKit.checkBio') },
        {
            key: 'reach',
            done: Boolean(metrics?.avg_reach || metrics?.engagement_rate),
            label: t('mediaKit.checkReach'),
        },
        { key: 'audience', done: (kit?.audience_countries?.length || 0) > 0, label: t('mediaKit.checkAudience') },
        { key: 'samples', done: (kit?.sample_content?.length || 0) > 0, label: t('mediaKit.checkSamples') },
        {
            key: 'collabs',
            done: (kit?.past_collaborations?.length || 0) > 0 || (data?.platform_deals?.count || 0) > 0,
            label: t('mediaKit.checkCollabs'),
        },
        { key: 'rates', done: (data?.rate_card?.items?.length || 0) > 0, label: t('mediaKit.checkRates') },
    ];
}

export default function MediaKitPanel({ className = '' }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const load = () => api.get('/api/media-kit/')
        .then(res => setData(res.data))
        .catch(() => { })
        .finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    // One helper for both switches: publishing decides who may read the page,
    // `show_on_profile` decides whether the profile links to it. Two different
    // calls, so two switches — a creator can keep the page live for brands
    // reading an application without it sitting on their link-in-bio.
    const toggleFlag = async (flag) => {
        setPublishing(true);
        try {
            const res = await api.put('/api/media-kit/', { [flag]: !data?.kit?.[flag] });
            if (res.status === 200) setData(prev => ({ ...prev, kit: res.data.kit }));
        } catch (err) { /* surfaced by the interceptor */ }
        finally { setPublishing(false); }
    };

    if (loading) {
        return (
            <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#fafafa] ${className}`}>
                <div className="flex justify-center py-6"><span className="spinner-sm" /></div>
            </section>
        );
    }

    const kit = data?.kit;
    const rows = checklist(data, t);
    const done = rows.filter(row => row.done).length;
    const percent = Math.round((done / rows.length) * 100);
    const published = Boolean(kit?.is_public);

    return (
        <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#fafafa] ${className}`}>
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                {t('mediaKit.title')}
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ml-auto border ${published
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                    {published ? t('mediaKit.statusPublished') : t('mediaKit.statusDraft')}
                </span>
            </h2>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">{t('mediaKit.subtitle')}</p>

            {/* Numbers as they stand */}
            {data?.metrics?.accounts > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                        <p className="text-base font-black text-slate-800 tabular-nums">{compact(data.metrics.followers)}</p>
                        <p className="text-[10px] text-slate-500 font-semibold">{t('profile.followers')}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                        <p className="text-base font-black text-slate-800 tabular-nums">{compact(data.metrics.avg_reach)}</p>
                        <p className="text-[10px] text-slate-500 font-semibold">{t('mediaKit.reach')}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                        <p className="text-base font-black text-slate-800 tabular-nums">%{data.metrics.engagement_rate}</p>
                        <p className="text-[10px] text-slate-500 font-semibold">{t('mediaKit.engagement')}</p>
                    </div>
                </div>
            )}

            {/* Completeness, with what is still missing spelled out */}
            <div className="mb-4">
                <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold text-slate-600">{t('mediaKit.completeness')}</span>
                    <span className="font-black text-slate-700 tabular-nums">{percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600 transition-all duration-500"
                        style={{ width: `${percent}%` }} />
                </div>
                <ul className="mt-2.5 space-y-1">
                    {rows.map(row => (
                        <li key={row.key} className={`flex items-center gap-2 text-[11px] ${row.done ? 'text-slate-400' : 'text-slate-600 font-semibold'}`}>
                            <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 text-[8px] ${row.done ? 'bg-primary-500 text-white' : 'bg-slate-200 text-transparent'}`}>✓</span>
                            <span className={row.done ? 'line-through' : ''}>{row.label}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Attached to every application whether or not it is published —
                that is the part worth saying out loud. */}
            <p className="text-[11px] text-primary-800 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2 leading-relaxed mb-4">
                {t('mediaKit.autoAttachNote')}
            </p>

            <div className="flex flex-wrap gap-2">
                <button onClick={() => setEditing(true)} className="btn-primary flex-1 py-2.5 text-sm">
                    {kit ? t('mediaKit.editBtn') : t('mediaKit.createBtn')}
                </button>
                {data?.username && (
                    <Link to={`/${data.username}/media-kit`} target="_blank" rel="noopener noreferrer"
                        className="btn-secondary py-2.5 px-4 text-sm no-underline">
                        {t('mediaKit.previewBtn')}
                    </Link>
                )}
            </div>

            {kit && (
                <>
                    <button onClick={() => toggleFlag('is_public')} disabled={publishing}
                        className={`w-full mt-2 py-2 rounded-xl text-xs font-bold transition ${published
                            ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                            : 'text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'}`}>
                        {published ? t('mediaKit.unpublishBtn') : t('mediaKit.publishBtn')}
                    </button>

                    {/* Only meaningful once the page is published — an
                        unpublished kit has nothing to link to, and offering
                        the switch there would read as a second publish. */}
                    <label className={`flex items-start justify-between gap-3 mt-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 transition ${published ? 'cursor-pointer' : 'opacity-50'}`}>
                        <span className="min-w-0">
                            <span className="block text-xs font-bold text-slate-700">
                                {t('mediaKit.showOnProfile')}
                            </span>
                            <span className="block text-[11px] text-slate-400 leading-relaxed mt-0.5">
                                {t('mediaKit.showOnProfileHint')}
                            </span>
                        </span>
                        <input type="checkbox"
                            checked={Boolean(kit.show_on_profile)}
                            disabled={publishing || !published}
                            onChange={() => toggleFlag('show_on_profile')}
                            className="w-4 h-4 mt-0.5 accent-[#20c9b5] flex-shrink-0" />
                    </label>
                </>
            )}

            {editing && (
                <MediaKitEditor data={data} onClose={() => setEditing(false)} onSaved={load} />
            )}
        </section>
    );
}
